import { mergeAttributes, Node } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { TEMPLATE_IMAGE_ID_ATTR } from './templateImageAttrs.ts';
import { imageAlignToCss, parseImageAlign } from './imageAlign.ts';
import TemplateImageNodeView from './TemplateImageNodeView.tsx';

export interface SetTemplateImageOptions {
  src: string;
  alt?: string;
  templateImageId: string;
  // Display width in px. Persisted as the `width` attribute (survives save +
  // sanitizer) and is the most email-client-safe way to size an image.
  width?: number;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    templateImage: {
      setTemplateImage: (options: SetTemplateImageOptions) => ReturnType;
    };
  }
}

// Block-level atom node for tenant template images. Carries the backend image
// reference in `templateImageId` (serialized as `data-template-image-id`) while
// `src` holds the signed preview URL for in-editor display. The converters swap
// the `src` for `{{image '<id>'}}` on save and back on load. `width` (px) is
// kept as a plain `width` attribute so it survives the save/sanitizer roundtrip
// and renders correctly in emails. A React NodeView adds the drag-to-resize
// handle; `renderHTML` still drives `getHTML()` / the wire format.
export const TemplateImage = Node.create({
  name: 'templateImage',
  // Inline (not block) so it lives inside paragraph text — the caret/drop can
  // land it between letters, not only between lines. Float/align still work; a
  // centered image just renders on its own line via the wrapper's `display`.
  group: 'inline',
  inline: true,
  atom: true,
  draggable: true,
  selectable: true,

  addAttributes() {
    return {
      src: { default: null },
      alt: { default: null },
      width: {
        default: null,
        parseHTML: (element) => {
          const value = element.getAttribute('width');
          return value ? parseInt(value, 10) : null;
        },
        renderHTML: (attributes) =>
          attributes.width ? { width: attributes.width } : {},
      },
      templateImageId: {
        default: null,
        parseHTML: (element) => element.getAttribute(TEMPLATE_IMAGE_ID_ATTR),
        renderHTML: (attributes) =>
          attributes.templateImageId
            ? { [TEMPLATE_IMAGE_ID_ATTR]: attributes.templateImageId }
            : {},
      },
      // Positioning (left/center/right/wrap-left/wrap-right) serialized as an
      // inline `style` on the `<img>`. `parseHTML` reads it back so the
      // alignment round-trips (open → save → identical output).
      align: {
        default: null,
        parseHTML: (element) => parseImageAlign(element),
        renderHTML: (attributes) =>
          attributes.align ? { style: imageAlignToCss(attributes.align) } : {},
      },
    };
  },

  parseHTML() {
    return [{ tag: `img[${TEMPLATE_IMAGE_ID_ATTR}]` }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'img',
      mergeAttributes(HTMLAttributes, { class: 'rich-text-image' }),
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(TemplateImageNodeView);
  },

  addProseMirrorPlugins() {
    const nodeName = this.name;
    return [
      new Plugin({
        key: new PluginKey('templateImageTableGuard'),
        props: {
          // Block dropping an image into a table cell. Repositioning between
          // text stays allowed; returning true cancels the default drop, so a
          // dragged image just stays where it was rather than landing in a cell.
          handleDrop: (view, event, slice) => {
            let carriesImage = false;
            slice.content.descendants((node) => {
              if (node.type.name === nodeName) carriesImage = true;
            });
            if (!carriesImage) return false;

            const dropPos = view.posAtCoords({
              left: event.clientX,
              top: event.clientY,
            });
            if (!dropPos) return false;

            const $pos = view.state.doc.resolve(dropPos.pos);
            for (let depth = $pos.depth; depth > 0; depth -= 1) {
              if ($pos.node(depth).type.spec.tableRole) {
                return true; // inside a table → reject the drop
              }
            }
            return false;
          },
        },
      }),
    ];
  },

  addCommands() {
    return {
      setTemplateImage:
        (options) =>
        ({ commands }) =>
          commands.insertContent({
            type: this.name,
            attrs: {
              src: options.src,
              alt: options.alt ?? null,
              width: options.width ?? null,
              templateImageId: options.templateImageId,
            },
          }),
    };
  },
});
